import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'

const EXEMPT_PATHS = ['/terms', '/privacy', '/settings', '/guidelines', '/cookie-policy']

// Keep in lockstep with the server validators in server/lib/username.js.
const USERNAME_RE = /^[a-z0-9_]{2,30}$/
// Placeholder handle minted by handle_new_user (migration 204) on a username
// collision at signup: 'verse_' || 32 lowercase hex. While a user holds one,
// the gate prompts them to pick a real handle.
const PLACEHOLDER_RE = /^verse_[0-9a-f]{32}$/

function getAge(dob) {
  const today = new Date()
  const birth = new Date(dob)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

export default function DobGate({ children }) {
  const { user, getToken, signOut, refreshProfile } = useAuth()
  const location = useLocation()
  const [status, setStatus] = useState('loading')
  const [dob, setDob] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState('')
  const [needsUsername, setNeedsUsername] = useState(false)
  const [usernameInput, setUsernameInput] = useState('')
  const [usernameError, setUsernameError] = useState('')

  const isExempt = EXEMPT_PATHS.includes(location.pathname)

  useEffect(() => {
    if (isExempt || !user) { setStatus('has-dob'); return }

    // No JWT fast-path — user_metadata.dob can be set when profiles.dob is
    // still null (the handle_new_user trigger bug fixed in migration 123).
    // Always ask the server, which now reads profiles.dob authoritatively.
    // The same call returns the username so we can detect a mig-204 placeholder.
    let cancelled = false
    ;(async () => {
      try {
        const token = await getToken()
        if (!token) { setStatus('has-dob'); return } // session not yet populated; fail open
        const { data } = await axios.get('/api/user/dob-status', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled) return
        setStatus(data.hasDob ? 'has-dob' : 'needs-dob')
        setNeedsUsername(PLACEHOLDER_RE.test(data.username ?? ''))
      } catch {
        if (!cancelled) setStatus('has-dob') // fail open — write endpoints are server-gated
      }
    })()
    return () => { cancelled = true }
  }, [user, getToken])

  async function handleSubmit(e) {
    e.preventDefault()
    setFieldError('')
    setUsernameError('')

    // Validate the dob step only when it's actually being shown.
    if (status === 'needs-dob') {
      if (!dob) { setFieldError('Please enter your date of birth.'); return }
      const age = getAge(dob)
      if (age < 0 || new Date(dob) > new Date()) {
        setFieldError('Please enter a valid date of birth.')
        return
      }
    }

    // Validate the username step only when needed.
    let cleanUsername = null
    if (needsUsername) {
      cleanUsername = usernameInput.trim().toLowerCase()
      if (!USERNAME_RE.test(cleanUsername)) {
        setUsernameError('Username must be 2–30 characters: letters, numbers, and underscores.')
        return
      }
      if (cleanUsername.startsWith('verse_')) {
        setUsernameError('Usernames cannot start with “verse_”.')
        return
      }
    }

    setSubmitting(true)
    try {
      const token = await getToken()
      const cfg = { headers: { Authorization: `Bearer ${token}` } }

      // DOB first — the COPPA path may delete the account, in which case the
      // username write must never run. Once dob-submit succeeds we flip status
      // to 'has-dob', so a later username-only retry never re-fires dob-submit
      // (which would 409 "Date of birth already set" and confuse the flow).
      if (status === 'needs-dob') {
        await axios.post('/api/user/dob-submit', { dob }, cfg)
        setStatus('has-dob')
      }

      // Username second. A 400/409 here keeps the gate up showing ONLY the
      // username field (dob is already saved) for a username-only retry.
      if (needsUsername) {
        try {
          await axios.patch('/api/user/username', { username: cleanUsername }, cfg)
          setNeedsUsername(false)
        } catch (uErr) {
          setUsernameError(uErr.response?.data?.error || 'Could not set username. Please try again.')
          return
        }
      }

      // Refresh the cached profile so nav/avatar drop the placeholder.
      await refreshProfile?.()
    } catch (err) {
      if (err.response?.data?.code === 'COPPA_BLOCK') {
        setStatus('coppa-blocked')
      } else {
        setFieldError(err.response?.data?.error || 'Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (status === 'coppa-blocked') {
    return (
      <div className="dob-gate">
        <div className="dob-gate__card">
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>&#128274;</div>
          <h1 className="dob-gate__title">You must be 13 or older</h1>
          <p className="dob-gate__desc">
            We're sorry, but Verse is only available to users aged 13 and older.
            This is required by U.S. law (COPPA) to protect children's privacy online.
          </p>
          <p className="dob-gate__desc" style={{ fontSize: '0.875rem', marginTop: 8 }}>
            If you believe this is a mistake, please ask a parent or guardian to contact us at{' '}
            <a href="mailto:support@versesite.net" style={{ color: 'var(--purple)' }}>support@versesite.net</a>.
          </p>
          <button className="btn btn-primary dob-gate__btn" onClick={() => signOut()}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // Pass straight through unless the user needs to set a DOB or replace a
  // placeholder username. A non-placeholder, dob-set user (the overwhelming
  // majority) evaluates status==='has-dob' + needsUsername===false → renders
  // children, no gate shown. During the loading tick needsUsername is false too.
  const showGate = status === 'needs-dob' || needsUsername
  if (!showGate) return children

  const today = new Date().toISOString().split('T')[0]
  const showDob = status === 'needs-dob'

  return (
    <div className="dob-gate">
      <div className="dob-gate__card">
        <h1 className="dob-gate__title">{showDob ? 'Verify your age' : 'Choose your username'}</h1>
        <p className="dob-gate__desc">
          {showDob
            ? "To continue using Verse, please enter your date of birth. This is required to comply with children's privacy laws."
            : 'Pick a username for your Verse account. This is how other writers will find you.'}
        </p>
        <form onSubmit={handleSubmit}>
          {showDob && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label className="dob-gate__label" htmlFor="dob-gate-input">Date of birth</label>
                <input
                  id="dob-gate-input"
                  type="date"
                  className="dob-gate__input"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={today}
                  autoComplete="bday"
                />
              </div>
              {dob && getAge(dob) >= 13 && getAge(dob) < 18 && (
                <div className="dob-gate__teen-notice">
                  Teen account — Mature content will be hidden
                </div>
              )}
              {fieldError && <p className="dob-gate__error">{fieldError}</p>}
            </>
          )}
          {needsUsername && (
            <div style={{ marginBottom: 16 }}>
              <label className="dob-gate__label" htmlFor="dob-gate-username">Username</label>
              <input
                id="dob-gate-username"
                type="text"
                className="dob-gate__input"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                placeholder="your_pen_name"
                autoComplete="username"
                maxLength={30}
              />
              <p className="dob-gate__note" style={{ marginTop: 6 }}>
                2–30 characters: letters, numbers, and underscores.
              </p>
              {usernameError && <p className="dob-gate__error">{usernameError}</p>}
            </div>
          )}
          <button
            type="submit"
            className="btn btn-primary dob-gate__btn"
            disabled={submitting || (showDob && !dob) || (needsUsername && !usernameInput)}
          >
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </form>
        <p className="dob-gate__note">
          {showDob
            ? 'Your date of birth is used to determine account type and is stored securely. This verification is recorded with your account, timestamp, and IP address.'
            : 'You can change your username later from settings.'}
        </p>
      </div>
    </div>
  )
}
